import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { API_URL } from '../config/api';

const API = API_URL.replace(/\/$/, '');

export default function AdminUsers() {
  const [users, setUsers] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [openTable, setOpenTable] = useState(true);
  const [search, setSearch] = useState('');
  const [restrictMinutes, setRestrictMinutes] = useState<number | 'indefinite'>(60);
  const [userFilter, setUserFilter] = useState<'all' | 'real' | 'seeded'>('all');
  const [createForm, setCreateForm] = useState<{ name?: string; email?: string; password?: string; role?: string; username?: string }>({ role: 'admin' });
  const [creating, setCreating] = useState(false);
  const token = typeof window !== 'undefined' ? localStorage.getItem('token') : null;

  async function loadUsers() {
    try {
      setLoading(true); setErr(null);
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const u = await axios.get(`${API}/admin/users`, { headers });
      setUsers(u.data || []);
    } catch (e: any) {
      setErr(e?.response?.data?.message || e?.message || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { loadUsers(); }, []);

  const filteredUsers = useMemo(() => {
    let list = users;
    if (userFilter === 'seeded') list = list.filter((u) => u.seeded);
    if (userFilter === 'real') list = list.filter((u) => !u.seeded);
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      list = list.filter((u) => String(u.username || '').toLowerCase().includes(q) || String(u.id || '').toLowerCase().includes(q));
    }
    return list;
  }, [users, userFilter, search]);

  const DURATIONS: Array<{ label: string; value: number | 'indefinite' }> = [
    { label: '15m', value: 15 },
    { label: '30m', value: 30 },
    { label: '1h', value: 60 },
    { label: '6h', value: 360 },
    { label: '24h', value: 1440 },
    { label: '7d', value: 10080 },
    { label: '30d', value: 43200 },
    { label: 'Indefinite', value: 'indefinite' },
  ];

  function isUserRestricted(u: any): boolean {
    if (!u?.restrictedUntil) return false;
    try {
      return new Date(u.restrictedUntil).getTime() > Date.now();
    } catch {
      return false;
    }
  }

  async function toggleRestrict(u: any) {
    if (isUserRestricted(u)) {
      await unrestrictUser(u._id);
    } else {
      await restrictUser(u._id);
    }
  }

  async function restrictUser(userId: string) {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const payload: any = { reason: 'policy_violation' };
      if (restrictMinutes === 'indefinite') payload.indefinite = true; else payload.minutes = restrictMinutes;
      await axios.post(`${API}/admin/users/${userId}/restrict`, payload, { headers });
      await loadUsers();
    } catch (e) {}
  }
  async function unrestrictUser(userId: string) {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.post(`${API}/admin/users/${userId}/unrestrict`, {}, { headers });
      await loadUsers();
    } catch (e) {}
  }
  async function deleteUser(userId: string) {
    if (!confirm('Delete this user? This cannot be undone.')) return;
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.delete(`${API}/admin/users/${userId}`, { headers });
      await loadUsers();
    } catch (e: any) {
      alert(e?.response?.data?.message || 'Failed to delete user');
    }
  }
  async function setRole(userId: string, role: string) {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.post(`${API}/admin/users/${userId}/role`, { role }, { headers });
      await loadUsers();
    } catch (e: any) {
      alert(e?.response?.data?.message || 'Failed to set role (super_admin required)');
    }
  }
  async function createUser(e: React.FormEvent) {
    e.preventDefault();
    try {
      setCreating(true);
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await axios.post(`${API}/admin/users`, createForm, { headers });
      setCreateForm({ role: 'admin' });
      await loadUsers();
      alert(`User created: ${res.data?.user?.email}`);
    } catch (e: any) {
      alert(e?.response?.data?.message || 'Failed to create user (super_admin required)');
    } finally {
      setCreating(false);
    }
  }

  return (
    <div className="p-6 space-y-6 themed-page">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-heading">Admin — Users</h2>
        <button className="btn text-xs" onClick={loadUsers}>Refresh</button>
      </div>
      {err && <div className="text-red-500 text-sm">{err}</div>}

      {/* Create user (super_admin only) */}
      <section className="p-4 rounded-xl themed-card">
        <h3 className="text-sm font-semibold text-heading">Create Admin / Super Admin</h3>
        <form onSubmit={createUser} className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-2">
          <input className="input px-2 py-1 text-sm rounded-md" placeholder="Name" value={createForm.name || ''} onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })} />
          <input className="input px-2 py-1 text-sm rounded-md" placeholder="Email" value={createForm.email || ''} onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })} />
          <input className="input px-2 py-1 text-sm rounded-md" placeholder="Username (optional)" value={createForm.username || ''} onChange={(e) => setCreateForm({ ...createForm, username: e.target.value })} />
          <input className="input px-2 py-1 text-sm rounded-md" placeholder="Password" type="password" value={createForm.password || ''} onChange={(e) => setCreateForm({ ...createForm, password: e.target.value })} />
          <select className="input px-2 py-1 text-sm rounded-md" value={createForm.role || 'admin'} onChange={(e) => setCreateForm({ ...createForm, role: e.target.value })}>
            <option value="admin">admin</option>
            <option value="super_admin">super_admin</option>
          </select>
          <button className="btn text-xs" type="submit" disabled={creating}>{creating ? 'Creating…' : 'Create'}</button>
        </form>
        <div className="text-[11px] text-theme-secondary mt-2">Note: Only super_admin can create or delete users and set roles.</div>
      </section>

      {/* Users table */}
      <section className="p-4 rounded-xl themed-card space-y-3">
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <span className="text-xs opacity-70">Filter:</span>
            {(['all','real','seeded'] as const).map((f) => (
              <button key={f} onClick={() => setUserFilter(f)} className={`px-3 py-1 rounded-md text-xs ${f === userFilter ? 'themed-card' : 'border'}`} style={{ borderColor: f === userFilter ? undefined : 'var(--border)' }}>{f}</button>
            ))}
          </div>
          <div className="flex items-center gap-2">
            <input className="input px-2 py-1 text-xs rounded-md" placeholder="Search username or Anon ID" value={search} onChange={(e) => setSearch(e.target.value)} />
            <button className="btn text-xs" onClick={() => setOpenTable((v) => !v)}>{openTable ? 'Hide' : 'Show'}</button>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs opacity-70">Restrict:</span>
            <select className="input px-2 py-1 text-xs rounded-md" value={String(restrictMinutes)} onChange={(e) => setRestrictMinutes((e.target.value === 'indefinite') ? 'indefinite' : parseInt(e.target.value, 10) || 60)}>
              {DURATIONS.map((d) => (
                <option key={String(d.value)} value={String(d.value)}>{d.label}</option>
              ))}
            </select>
            <input type="number" min={5} placeholder="Custom (min)" className="input px-2 py-1 text-xs rounded-md w-28" onBlur={(e) => { const v = parseInt(e.target.value, 10); if (Number.isFinite(v) && v > 0) setRestrictMinutes(v); e.currentTarget.value = ''; }} />
          </div>
        </div>
        {openTable && (
        <div className="mt-2 max-h-80 overflow-y-auto pr-1">
        <table className="w-full text-xs text-heading">
          <thead>
              <tr className="text-heading sticky top-0 z-10" style={{ background: 'var(--card)', boxShadow: '0 1px 0 var(--border)' }}>
              <th className="text-left">Anon ID</th>
              <th className="text-left">Username</th>
              <th>Role</th>
              <th>Seeded</th>
                <th>Restricted Until</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredUsers.map((u) => (
              <tr key={u._id} className="border-t hover:opacity-95 transition odd:bg-gray-50 dark:odd:bg-gray-800" style={{ borderColor: 'var(--border)' }}>
                <td>{u.id}</td>
                <td className="text-theme-secondary">
                  {u.username}
                  {u.seeded && (
                    <span className="ml-2 px-2 py-0.5 rounded-full text-[10px] border bg-purple-500/10 text-purple-400" style={{ borderColor: 'var(--border)' }}>seeded</span>
                  )}
                </td>
                <td>
                  <select className="input px-2 py-1 text-[11px] rounded-md min-w-[160px]" value={u.role} onChange={(e) => setRole(u._id, e.target.value)}>
                    <option value="user">user</option>
                    <option value="event_creator">event_creator</option>
                    <option value="venue_owner">venue_owner</option>
                    <option value="admin">admin</option>
                    <option value="super_admin">super_admin</option>
                  </select>
                </td>
                <td>{u.seeded ? 'yes' : 'no'}</td>
                <td>{u.restrictedUntil ? new Date(u.restrictedUntil).toLocaleString() : '-'}</td>
                <td>
                  <div className="flex flex-wrap items-center gap-2">
                    <button
                      className={`${isUserRestricted(u) ? 'bg-red-500/15 text-red-400' : 'bg-emerald-500/15 text-emerald-400'} px-3 py-1 rounded-full text-xs border`}
                      style={{ borderColor: 'var(--border)' }}
                      title={isUserRestricted(u) ? 'Click to unrestrict' : `Click to restrict for ${restrictMinutes === 'indefinite' ? '∞' : restrictMinutes + 'm'}`}
                      onClick={() => toggleRestrict(u)}
                    >
                      {isUserRestricted(u) ? 'Restricted' : 'Active'}
                    </button>
                    <button
                      className="px-3 py-1 rounded-lg border text-xs"
                      style={{ borderColor: 'var(--border)', color: 'var(--danger, #ef4444)' }}
                      onClick={() => {
                        const name = String(u?.username || u?.id || '').trim();
                        if (confirm(`Delete user${name ? ` "${name}"` : ''}? This cannot be undone.`)) deleteUser(u._id);
                      }}
                    >
                      Delete
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
        )}
      </section>
    </div>
  );
}
